import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, User, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const PRED_LABEL = {
  KESIN: "Kesin bizden", BELKI: "Belki", BELIRSIZ: "Belirsiz", GELMEYECEK: "Gelmeyecek",
  ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "Boş",
};
const COLOR = { biz: "#16A34A", karsi: "#DC2626", belirsiz: "#F59E0B", gelmedi: "#64748B" };
const outcomeOf = (r) => (!r.marked ? "gelmedi"
  : r.tally_group === "biz" ? "biz" : r.tally_group === "karsi" ? "karsi" : "belirsiz");

export default function PersonCard({ name, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/election-day/reconcile/person", { params: { name } });
        if (alive) setD(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail, e));
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [name, onClose]);

  const s = d?.stats;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/60 p-3" onClick={onClose}
      data-testid="person-card">
      <Card className="mx-auto max-w-4xl p-4" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Kişi kartı hazırlanıyor...
          </div>
        ) : d && (
          <>
            <div className="flex flex-wrap items-start gap-2">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0F2C59] text-white">
                <User className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-lg font-bold">{d.person}</h3>
                <p className="text-xs text-muted-foreground">
                  Oy gücü <b>{s.power}</b> firma · {s.committees.length} komite
                  {s.rank ? ` · oy gücü sıralaması #${s.rank}` : ""} ·
                  {" "}komiteler: {s.committees.map((c) => `#${c}`).join(", ")}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="person-card-close">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {d.outcome_labels.map((o) => (
                <div key={o.key} className="rounded-lg border border-border p-3"
                  style={{ borderLeftWidth: 4, borderLeftColor: COLOR[o.key] }}
                  data-testid={`person-box-${o.key}`}>
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">{o.label}</p>
                  <p className="font-display text-2xl font-bold" style={{ color: COLOR[o.key] }}>
                    {s.outcomes[o.key]}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-3 rounded-lg bg-muted/50 p-3 text-sm" data-testid="person-verdict">
              <b>Davranış özeti:</b> Tahminimizde <b>{s.pred_kesin}</b> firması kesin bizdendi.
              Sandıkta bize <b>{s.outcomes.biz}</b>, karşıya <b>{s.outcomes.karsi}</b>,
              belirsiz <b>{s.outcomes.belirsiz}</b> oy kullandı;
              {" "}<b>{s.outcomes.gelmedi}</b> firmasıyla sandığa gelmedi.
              Tahmin uyumu <b>{s.uyum}</b> firma, sapma <b>{s.sapma}</b> firma.
              {" "}Gerçekleşme <b>%{s.realized_pct}</b>, tutarlılık <b>%{s.score}</b>.
              {s.suspicious > 0 && (
                <span className="ml-1 font-semibold text-red-600">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  {s.suspicious} firmada şüpheli sapma (bizden sayılıp karşıya işaretlendi).
                </span>
              )}
            </div>

            <ScrollArea className="mt-3 max-h-[380px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left">
                    {["Firma", "Komite", "Şube", "2026 Tahmini", "Çetele", "Uyum",
                      "İşaretleyen", "Neden / Not"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => {
                    const col = COLOR[outcomeOf(r)];
                    return (
                      <tr key={r.id} className={`border-b border-border/60 ${r.suspicious ? "bg-red-50 dark:bg-red-950/30" : ""}`}
                        data-testid={`person-firm-${r.id}`}>
                        <td className="max-w-[240px] px-2 py-1.5">
                          <span className="block truncate font-medium"
                            style={{ borderLeft: `3px solid ${col}`, paddingLeft: 6 }}>
                            {r.company_name}
                          </span>
                          <span className="pl-2 text-[10px] text-muted-foreground">{r.sicil_no}</span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">#{r.committee_no}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{r.branch}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{PRED_LABEL[r.prediction] ?? r.prediction}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 font-semibold" style={{ color: col }}>
                          {r.marked ? (PRED_LABEL[r.tally] ?? r.tally ?? "—") : "Gelmedi"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {r.match === "UYUM" ? "Uyumlu" : r.match === "SAPMA" ? "Sapma"
                            : r.match === "BEKLEYEN" ? "—" : "Eğilim yok"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">
                          {r.marked_by || "—"} {r.marked_at ? String(r.marked_at).slice(11, 16) : ""}
                        </td>
                        <td className="max-w-[160px] truncate px-2 py-1.5 text-muted-foreground">
                          {(r.reason || "") + (r.note ? ` · ${r.note}` : "") || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          </>
        )}
      </Card>
    </div>
  );
}
